import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CloudFilters, FacetDef, SortOption } from './cloud-filter.model';

export interface FilterPill {
  facetKey: string;
  value: string;
  label: string;
}

@Component({
  selector: 'app-cloud-filter-bar',
  templateUrl: './cloud-filter-bar.component.html',
})
export class CloudFilterBarComponent implements OnChanges, OnDestroy {
  @Input() entityName = 'items';
  @Input() placeholder = 'Search…';
  @Input() facets: FacetDef[] = [];
  @Input() sortOptions: SortOption[] = [];
  @Input() filters!: CloudFilters;
  @Input() resultCount = 0;
  @Input() totalCount = 0;
  @Input() quotaUsage: { currentCount: number; limit: number; remaining: number; percentUsed: number } | null = null;
  @Output() filtersChange = new EventEmitter<CloudFilters>();

  searchQuery = '';
  currentSort = '';
  selectedFacets: Record<string, string[]> = {};
  mobileOpen = false;
  isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  openDropdown: string | null = null;
  dropdownPosition = { top: 0, left: 0 };

  private readonly querySubject = new Subject<string>();
  private readonly querySub: Subscription;

  constructor(private readonly elRef: ElementRef) {
    this.querySub = this.querySubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
    ).subscribe(q => this.emit({ ...this.snapshot, query: q }));
  }

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 640;
    if (!this.isMobile) this.mobileOpen = false;
    this.openDropdown = null;
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(): void {
    this.openDropdown = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.openDropdown = null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filters'] && this.filters) {
      this.searchQuery    = this.filters.query;
      this.currentSort    = this.filters.sort;
      this.selectedFacets = { ...this.filters.facets };
    }
  }

  ngOnDestroy(): void {
    this.querySub.unsubscribe();
  }

  get facetsVisible(): boolean {
    return !this.isMobile || this.mobileOpen;
  }

  get activeFilterPills(): FilterPill[] {
    const pills: FilterPill[] = [];
    for (const facet of this.facets) {
      for (const val of this.selectedFacets[facet.key] ?? []) {
        const opt = facet.options.find(o => o.value === val);
        pills.push({ facetKey: facet.key, value: val, label: `${facet.label}: ${opt?.label ?? val}` });
      }
    }
    return pills;
  }

  get activeFacetCount(): number {
    return this.activeFilterPills.length;
  }

  get hasActiveFacets(): boolean {
    return this.activeFacetCount > 0;
  }

  private get snapshot(): CloudFilters {
    return { query: this.searchQuery, sort: this.currentSort, facets: this.selectedFacets };
  }

  isSelected(facetKey: string, value: string): boolean {
    return (this.selectedFacets[facetKey] ?? []).includes(value);
  }

  onQueryChange(q: string): void {
    this.searchQuery = q;
    this.querySubject.next(q);
  }

  onSortChange(sort: string): void {
    this.currentSort = sort;
    this.emit({ ...this.snapshot, sort });
  }

  toggleFacet(facetKey: string, value: string): void {
    const current = [...(this.selectedFacets[facetKey] ?? [])];
    const idx = current.indexOf(value);
    if (idx >= 0) current.splice(idx, 1); else current.push(value);
    this.selectedFacets = { ...this.selectedFacets, [facetKey]: current };
    this.emit(this.snapshot);
  }

  toggleDropdown(facetKey: string, triggerEl: HTMLElement): void {
    if (this.openDropdown === facetKey) {
      this.openDropdown = null;
      return;
    }
    const rect = triggerEl.getBoundingClientRect();
    this.dropdownPosition = { top: rect.bottom + 4, left: rect.left };
    this.openDropdown = facetKey;
  }

  selectedCount(facetKey: string): number {
    return (this.selectedFacets[facetKey] ?? []).length;
  }

  isSelectedAny(facetKey: string): boolean {
    return this.selectedCount(facetKey) > 0;
  }

  removePill(pill: FilterPill): void {
    this.toggleFacet(pill.facetKey, pill.value);
  }

  clearAll(): void {
    this.searchQuery    = '';
    this.currentSort    = this.sortOptions[0]?.value ?? '';
    this.selectedFacets = {};
    this.querySubject.next('');
    this.emit(this.snapshot);
  }

  private emit(f: CloudFilters): void {
    this.filtersChange.emit(f);
  }
}
